import { useEffect, useState } from 'react';

/**
 * Hook to manage the open state of a content section (knowledge, admission, lab) section
 * @param {object} props
 * @param {boolean} props.isContainsActiveBinder - Whether the section contains the active binder
 * @param {boolean} props.isFirst - Whether the section is the first one
 * @param {string | undefined} props.id - The section ID, if any
 * @returns {{isOpen: boolean, onToggle: (open: boolean) => void}} The state and the toggle function
 */
export function useSectionOpenState({ isContainsActiveBinder, isFirst, id = '' }) {
  // Check if there's a hash in the URL targeting a specific section
  const hasHashTarget = window.location.hash && window.location.hash.length > 1;

  // Open the first section by default (unless there's a hash target),
  // or if it contains the active binder
  const isSectionInitiallyOpen = (isFirst && !hasHashTarget) || isContainsActiveBinder;

  const [isOpen, setIsOpen] = useState(isSectionInitiallyOpen);
  const [isUserToggled, setIsUserToggled] = useState(false);

  useEffect(() => {
    // Only update if the user hasn't manually toggled the section
    if (!isUserToggled) {
      setIsOpen(isSectionInitiallyOpen);
    }
  }, [isSectionInitiallyOpen, isUserToggled]);

  useEffect(() => {
    // Auto-open section when it contains the active binder (e.g., from navigation)
    if (isContainsActiveBinder && !isOpen) {
      setIsOpen(true);
    }
  }, [isContainsActiveBinder, isOpen]);

  useEffect(() => {
    if (id && window.location.hash === `#${id}`) {
      setIsOpen(true);
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }, 200);
      }
    }
    // eslint-disable-next-line
  }, [id, window.location.hash]);

  const onToggle = open => {
    setIsOpen(open);
    setIsUserToggled(true); // Indicate that the user has manually toggled the section
  };

  return { isOpen, onToggle };
}
